import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { PayrollSettingsModal, PayrollCalculationPreview } from '../components/PayrollCalculations';
import GradeConfiguration from '../components/GradeConfiguration';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function Payroll() {
  const { user } = useAuth();
  const [pendingEntries, setPendingEntries] = useState([]);
  const [showPending, setShowPending] = useState(false);
  const [view, setView] = useState('runs');
  const [showCalculationSettings, setShowCalculationSettings] = useState(false);
  const [showGradeConfiguration, setShowGradeConfiguration] = useState(false);
  const [showCalculationPreview, setShowCalculationPreview] = useState(false);
  // PAYSLIP REVIEW PUBLICATION UI
  const [publishingPayslips, setPublishingPayslips] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ bank_account_id:'', payment_date:'', reference:'' });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [runs, setRuns] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);
  const [entries, setEntries] = useState([]);
  const [showNewRun, setShowNewRun] = useState(false);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [viewEntry, setViewEntry] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [exceptionsLoading, setExceptionsLoading] = useState(false);
  const [showExceptions, setShowExceptions] = useState(false);

  const canEdit = ['super_admin','admin','hr_officer'].includes(user?.role);

  const usesAttendanceCalculations = selectedRun &&
    (Number(selectedRun.year) * 12 + Number(selectedRun.month) >= 2026 * 12 + 10);
  const runEditable = canEdit && selectedRun?.status === 'draft' && !selectedRun?.payslips_published_at;

  const togglePayslipPublication = async () => {
    if (!selectedRun || publishingPayslips) return;

    const published =
      Boolean(
        selectedRun.payslips_published_at
      );

    const action =
      published
        ? 'withdraw'
        : 'publish';

    const confirmed =
      window.confirm(
        published
          ? 'Withdraw these draft payslips from staff view? Paid payslips will remain visible.'
          : 'Publish these draft payslips for staff review?\n\nStaff will see their earnings, deductions and net pay marked Awaiting Payment.'
      );

    if (!confirmed) return;

    setPublishingPayslips(true);

    try {
      const result = await put(
        '/payroll/runs/' +
          selectedRun.id +
          '/payslips/' +
          action,
        {}
      );

      const updated =
        result.data || selectedRun;

      setSelectedRun(updated);

      setRuns(current =>
        current.map(run =>
          run.id === updated.id
            ? updated
            : run
        )
      );

      alert(
        result.message ||
        (
          published
            ? 'Payslip review withdrawn.'
            : 'Payslips published for review.'
        )
      );
    } catch (error) {
      alert(
        error.message ||
        'Payslip publication could not be updated.'
      );
    } finally {
      setPublishingPayslips(false);
    }
  };


  const loadExceptions = async (runId) => {
    setExceptionsLoading(true);
    try {
      const result = await get('/payroll/runs/'+runId+'/exceptions');
      setExceptions(result.data||[]);
      setShowExceptions(true);
    } catch(err) { alert(err.message); }
    finally { setExceptionsLoading(false); }
  };

  const loadRuns = async () => {
    setLoading(true);

    try {
      const result =
        await get('/payroll/runs');

      const nextRuns =
        result.data || [];

      setRuns(nextRuns);

      setSelectedRun(current => {
        if (!current) return current;

        return (
          nextRuns.find(
            run => run.id === current.id
          ) || current
        );
      });

      return nextRuns;
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = () => {
    get('/payroll/staff').then(d => setStaff(d.data||[]));
  };

  const loadEntries = (runId) => {
    return get('/payroll/runs/'+runId+'/entries').then(d => setEntries(Array.isArray(d?.data)?d.data:[]));
  };

  useEffect(() => {
    if (user?.role === 'super_admin') {
      get('/payroll/entries/pending').then(d => setPendingEntries(d.data||[])).catch(()=>{});
    }
  }, []);

  const handleApproveEntry = async (id) => {
    try { 
      await put('/payroll/entries/'+id+'/approve', {}); 
      get('/payroll/entries/pending').then(d => setPendingEntries(d.data||[]));
      alert('Payroll entry approved');
    } catch (err) { alert(err.message); }
  };

  const handleRejectEntry = async (id) => {
    const reason = window.prompt('Reason for rejection:');
    if (reason === null) return;
    try {
      await put('/payroll/entries/'+id+'/reject', { reason });
      get('/payroll/entries/pending').then(d => setPendingEntries(d.data||[]));
      alert('Payroll entry rejected');
    } catch (err) { alert(err.message); }
  };

  useEffect(() => { 
    loadRuns(); 
    loadStaff();
    get('/bank-accounts').then(d => setBankAccounts(d.data||[]));
  }, []);

  const handleSelectRun = (run) => {
    setSelectedRun(run);
    setView('entries');
    loadEntries(run.id);
  };

  const handleDeleteRun = async (id) => {
    if (!window.confirm('Delete this entire payroll run? All entries will be lost.')) return;
    try { await del('/payroll/runs/'+id); loadRuns(); setSelectedRun(null); setView('runs'); }
    catch (err) { alert(err.message); }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Delete this payroll entry?')) return;
    try { await del('/payroll/entries/'+id); loadEntries(selectedRun.id); loadRuns(); }
    catch (err) { alert(err.message); }
  };

  const handleGenerateAll = async () => {
    if (!selectedRun) return;
    if (!window.confirm('Auto-generate payroll entries for all active staff based on their base salary?')) return;
    setSaving(true);
    try {
      const result = await post('/payroll/runs/'+selectedRun.id+'/generate-all', {});
      alert(result.message);
      loadEntries(selectedRun.id);
      loadRuns();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const handleMarkAllPaid = () => {
    setPaymentForm({
      bank_account_id: bankAccounts.find(a=>a.is_default)?.id || '',
      payment_date: new Date().toISOString().split('T')[0],
      reference: ''
    });
    setShowPaymentModal(true);
  };

  const confirmMarkAllPaid = async () => {
    try {
      await put('/payroll/runs/'+selectedRun.id+'/mark-all-paid', paymentForm);
      setShowPaymentModal(false);
      loadEntries(selectedRun.id);
      loadRuns();
      alert('Payroll paid successfully. Gross pay was recorded in the payroll month and net pay was recorded against the bank payment date.');
    } catch(err) {
      setShowPaymentModal(false);
      if (err.message && err.message.includes('attendance discrepancy')) {
        alert('PAYROLL BLOCKED - ' + err.message);
      } else {
        alert(err.message);
      }
    }
  };

  return (
    <div>
      {/* Pending payroll entries */}
      {user?.role === 'super_admin' && pendingEntries.length > 0 && (
        <div style={{background:'#F0E6FF',border:'1px solid #D4B8FF',borderRadius:8,padding:'12px 20px',marginBottom:16}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:showPending?12:0}}>
            <div style={{fontSize:13,fontWeight:500,color:'#6B2FAD'}}>
              💼 <strong>{pendingEntries.length}</strong> payroll entr{pendingEntries.length!==1?'ies':'y'} awaiting your approval
            </div>
            <button className="btn btn-sm" style={{background:'#6B2FAD',color:'#fff',border:'none'}}
              onClick={()=>setShowPending(!showPending)}>
              {showPending?'Hide':'Review'}
            </button>
          </div>
          {showPending && (
            <div style={{background:'#fff',borderRadius:8,overflow:'hidden'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#6B2FAD'}}>
                  {['Staff','Month/Year','Base Pay','Gross Pay','Net Pay','Created By',''].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#fff',textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {pendingEntries.map((e,i) => (
                    <tr key={e.id} style={{background:i%2===0?'#fff':'#FAF5FF'}}>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{e.staff_name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{e.month}/{e.year}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif'}}>₦{Number(e.base_pay||0).toLocaleString()}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif'}}>₦{Number(e.gross_pay||0).toLocaleString()}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontWeight:600,color:'#556B2F'}}>₦{Number(e.net_pay||0).toLocaleString()}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{e.created_by_name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        <div style={{display:'flex',gap:6}}>
                          <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none',fontSize:11}}
                            onClick={()=>handleApproveEntry(e.id)}>✓ Approve</button>
                          <button className="btn btn-danger btn-sm" style={{fontSize:11}}
                            onClick={()=>handleRejectEntry(e.id)}>✗ Reject</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Payroll</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Monthly payroll management</p>
        </div>
        <div className="flex-center gap-sm">
          {user?.role==='super_admin' && <button className="btn btn-outline" onClick={()=>setShowCalculationSettings(true)}>Payroll Settings</button>}
          {canEdit && <button className="btn btn-outline" onClick={() => setShowAddStaff(true)}>Manage Staff</button>}
          {canEdit && <button className="btn btn-outline" onClick={() => setShowGradeConfiguration(true)}>Staff Grades</button>}
          {canEdit && <a className="btn btn-primary" href="/non-production-assessments">Staff Assessments</a>}
          {canEdit && <button className="btn btn-primary" onClick={() => setShowNewRun(true)}>+ New Payroll</button>}
          {view==='entries' && (
            <button className="btn btn-ghost" onClick={() => { setView('runs'); setSelectedRun(null); }}>Back to Runs</button>
          )}
          {view==='entries' && selectedRun && canEdit && (
            <button className="btn btn-outline btn-sm" style={{color:'#8F2E24',borderColor:'#8F2E24'}}
              onClick={()=>loadExceptions(selectedRun.id)} disabled={exceptionsLoading}>
              {exceptionsLoading?'Checking...':'⚠ Exceptions Report'}
            </button>
          )}
          {canEdit && view==='entries' && selectedRun && (
            <div style={{display:'flex',gap:8}}>
              {user?.role === 'super_admin' && (
                <button
                  className="btn btn-outline btn-sm"
                  onClick={
                    togglePayslipPublication
                  }
                  disabled={
                    publishingPayslips ||
                    selectedRun.status !==
                      'draft'
                  }
                  style={{
                    fontSize:12,
                    background:
                      selectedRun
                        .payslips_published_at
                        ? '#FDECEC'
                        : '#EDF3FF',
                    borderColor:
                      selectedRun
                        .payslips_published_at
                        ? '#8F2E24'
                        : '#3B5BDB',
                    color:
                      selectedRun
                        .payslips_published_at
                        ? '#8F2E24'
                        : '#3B5BDB'
                  }}
                >
                  {publishingPayslips
                    ? 'Updating...'
                    : selectedRun
                        .payslips_published_at
                      ? 'Withdraw Payslip Preview'
                      : 'Publish Payslips for Review'}
                </button>
              )}

              {user?.role==='super_admin' && usesAttendanceCalculations && (
                <button className="btn btn-outline btn-sm" onClick={()=>setShowCalculationPreview(true)}>
                  Review Earnings and Deductions
                </button>
              )}

              <button className="btn btn-primary" disabled={!runEditable} onClick={() => setEditEntry({_new:true})}>+ Add Staff Entry</button>
              {canEdit && selectedRun && entries.length === 0 && (
                <button className="btn btn-outline btn-sm"
                  style={{background:'#EDF3FF',color:'#3B5BDB',borderColor:'#3B5BDB'}}
                  onClick={handleGenerateAll} disabled={saving||!runEditable}>
                  ⚡ Generate All Staff
                </button>
              )}
              {canEdit && selectedRun && (
                <button className="btn btn-outline btn-sm"
                  style={{background:'#E8F5EB',color:'#2E6B3A',borderColor:'#2E6B3A'}}
                  onClick={handleMarkAllPaid} disabled={user?.role!=='super_admin'||selectedRun.status==='paid'||selectedRun.status==='rejected'}>
                  ✓ Mark All Paid
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {view==='runs' ? (
        <div>
          {loading ? <div className="spinner"></div> : runs.length === 0 ? (
            <div className="empty-state"><h3>No payroll runs yet</h3><p>Click New Payroll to start your first payroll run.</p></div>
          ) : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Period</th>
                    <th style={{textAlign:'right'}}>Gross Pay</th>
                    <th style={{textAlign:'right'}}>Deductions</th>
                    <th style={{textAlign:'right'}}>Net Pay</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map(r => (
                    <tr key={r.id}>
                      <td style={{fontWeight:500}}>{MONTHS[r.month-1]} {r.year}</td>
                      <td className="amt">{fmt(r.total_gross)}</td>
                      <td className="amt" style={{color:'var(--unpaid)'}}>{fmt(r.total_deductions)}</td>
                      <td className="amt" style={{color:'var(--paid)',fontWeight:700}}>{fmt(r.total_net)}</td>
                      <td><span className={'badge '+(r.status==='approved'?'b-paid':'b-grey')}>{r.status}</span></td>
                      <td>
                        <div className="flex-center gap-sm">
                          <button className="btn btn-ghost btn-sm" onClick={() => handleSelectRun(r)}>View</button>
                          {user?.role==='super_admin' && r.status==='draft' && !r.payslips_published_at && (
                            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteRun(r.id)}>Delete</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* Run summary */}
          {selectedRun && (
            <div style={{background:'#556B2F',borderRadius:10,padding:'16px 22px',marginBottom:16,display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:16}}>
              <div>
                <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'rgba(242,239,231,.5)'}}>Period</div>
                <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',color:'#A8893D',fontWeight:600,marginTop:4}}>{MONTHS[selectedRun.month-1]} {selectedRun.year}</div>
              </div>
              <div>
                <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'rgba(242,239,231,.5)'}}>Gross Pay</div>
                <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',color:'#F2EFE7',fontWeight:600,marginTop:4}}>{fmt(selectedRun.total_gross)}</div>
              </div>
              <div>
                <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'rgba(242,239,231,.5)'}}>Deductions</div>
                <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',color:'#F2EFE7',fontWeight:600,marginTop:4}}>{fmt(selectedRun.total_deductions)}</div>
              </div>
              <div>
                <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'rgba(242,239,231,.5)'}}>Net Pay</div>
                <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',color:'#A8893D',fontWeight:700,marginTop:4}}>{fmt(selectedRun.total_net)}</div>
              </div>
            </div>
          )}

          {selectedRun && !usesAttendanceCalculations && <p style={{background:'#FFF3DC',padding:12,borderRadius:8}}>This historical payroll retains its reviewed adjustments. Automatic earnings and penalty processing starts with October 2026.</p>}
          {selectedRun?.payslips_published_at && selectedRun.status==='draft' && <p style={{background:'#EDF3FF',padding:12,borderRadius:8}}>Payslips are published for staff review. Withdraw the preview before changing payroll amounts.</p>}
          {/* Entries table */}
          <div className="tbl-wrap">
            {entries.length === 0 ? (
              <div className="empty-state"><h3>No entries yet</h3><p>Add staff entries to this payroll run.</p></div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Role</th>
                    <th style={{textAlign:'right'}}>Base Pay</th>
                    <th style={{textAlign:'right'}}>Gross</th>
                    <th style={{textAlign:'right'}}>Deductions</th>
                    <th style={{textAlign:'right'}}>Net Pay</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(e => (
                    <tr key={e.id}>
                      <td style={{fontWeight:500}}>{e.staff_name}</td>
                      <td className="text-muted text-sm">{e.staff_role||'—'}</td>
                      <td className="amt">{fmt(e.base_pay)}</td>
                      <td className="amt">{fmt(e.gross_pay)}</td>
                      <td className="amt" style={{color:'var(--unpaid)'}}>{fmt(e.total_deductions)}</td>
                      <td className="amt" style={{color:'var(--paid)',fontWeight:700}}>{fmt(e.net_pay)}</td>
                      <td><span className={'badge '+(e.payment_status==='paid'?'b-paid':'b-grey')}>{e.payment_status}</span></td>
                      <td>
                        <div className="flex-center gap-sm">
                          <button className="btn btn-outline btn-sm" onClick={() => setViewEntry(e)}>Payslip</button>
                          {runEditable && e.payment_status!=='paid' && <button className="btn btn-ghost btn-sm" onClick={() => setEditEntry(e)}>Edit</button>}
                          {runEditable && e.payment_status!=='paid' && <button className="btn btn-danger btn-sm" onClick={() => handleDeleteEntry(e.id)}>Delete</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {showCalculationSettings && <PayrollSettingsModal staff={staff} onClose={()=>setShowCalculationSettings(false)} />}
      {showCalculationPreview && selectedRun && <PayrollCalculationPreview run={selectedRun} onClose={()=>setShowCalculationPreview(false)} onApplied={async()=>{await loadEntries(selectedRun.id);await loadRuns();}} />}
      {/* Mark All Paid Modal */}
      {showPaymentModal && selectedRun && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:460}}>
            <div className="modal-hdr">
              <span className="modal-title">Record Payroll Payment — {MONTHS[selectedRun.month-1]} {selectedRun.year}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowPaymentModal(false)}>X</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#F9F8F5',borderRadius:8,padding:12,marginBottom:16}}>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:13}}>
                  <span style={{color:'#8A8A8A'}}>Total Net Pay</span>
                  <span style={{fontWeight:700,color:'#556B2F'}}>{fmt(selectedRun.total_net)}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginTop:6}}>
                  <span style={{color:'#8A8A8A'}}>Staff Count</span>
                  <span style={{fontWeight:500}}>{entries.length} Stewards</span>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Bank Account *</label>
                <select className="form-control" value={paymentForm.bank_account_id}
                  onChange={e=>setPaymentForm({...paymentForm,bank_account_id:e.target.value})}>
                  <option value="">Select bank account</option>
                  {bankAccounts.map(a=>(
                    <option key={a.id} value={a.id}>{a.name} — {a.account_number}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Payment Date *</label>
                <input type="date" className="form-control" value={paymentForm.payment_date}
                  onChange={e=>setPaymentForm({...paymentForm,payment_date:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Reference / Batch Number</label>
                <input className="form-control" value={paymentForm.reference}
                  onChange={e=>setPaymentForm({...paymentForm,reference:e.target.value})}
                  placeholder="e.g. Transfer batch number" />
              </div>
              <div style={{background:'#FFF3DC',border:'1px solid #F0D9A0',borderRadius:8,
                padding:12,fontSize:12,color:'#8B5E0A'}}>
                This will record gross payroll of {fmt(selectedRun.total_gross)} as an expense in the payroll month and deduct net pay of {fmt(selectedRun.total_net)} from the selected bank account on the payment date.
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowPaymentModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmMarkAllPaid}
                disabled={!paymentForm.bank_account_id || !paymentForm.payment_date}>
                Confirm & Record Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewRun && <NewRunModal onClose={() => setShowNewRun(false)} onSaved={() => { setShowNewRun(false); loadRuns(); }} />}
      {showAddStaff && <ManageStaffModal staff={staff} canEdit={canEdit} onClose={() => setShowAddStaff(false)} onSaved={() => { loadStaff(); }} />}
      {showGradeConfiguration && <GradeConfiguration onClose={() => setShowGradeConfiguration(false)} />}
      {editEntry && (
        <PayrollEntryModal
          entry={editEntry._new ? null : editEntry}
          runId={selectedRun?.id}
          staff={staff}
          onClose={() => setEditEntry(null)}
          onSaved={() => { setEditEntry(null); loadEntries(selectedRun.id); loadRuns(); }} />
      )}
      {viewEntry && (
        <PayslipModal entry={viewEntry} run={selectedRun} onClose={() => setViewEntry(null)} />
      )}
    </div>
  );
}

function NewRunModal({ onClose, onSaved }) {
  const now = new Date();
  const [form, setForm] = useState({ month: now.getMonth()+1, year: now.getFullYear(), notes:'' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setSaving(true);
    try { await post('/payroll/runs', form); onSaved(); }
    catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:420}}>
        <div className="modal-hdr">
          <span className="modal-title">New Payroll Run</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Month *</label>
              <select className="form-control" value={form.month} onChange={e => setForm({...form,month:parseInt(e.target.value)})}>
                {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Year *</label>
              <input type="number" className="form-control" value={form.year}
                onChange={e => setForm({...form,year:parseInt(e.target.value)})} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.notes}
              onChange={e => setForm({...form,notes:e.target.value})} />
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving?'Creating...':'Create Payroll'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManageStaffModal({ staff, canEdit, onClose, onSaved }) {
  const [showForm, setShowForm] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const [users, setUsers] = useState([]);
  React.useEffect(() => { get('/users').then(d => setUsers(d.data||[])); }, []);
  const [form, setForm] = useState({
    name:'', email:'', phone:'', role:'', department:'',
    pay_type:'monthly', compensation_class:'staff', base_salary:'', daily_rate:'', hourly_rate:'',
    bank_name:'', bank_account_no:'', bank_account_name:'', date_joined:''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const openEdit = (s) => {
    setEditStaff(s);
    setForm({...s,compensation_class:s.compensation_class||'staff'});
    setShowForm(true);
  };

  const openNew = () => {
    setEditStaff(null);
    setForm({name:'',email:'',phone:'',role:'',department:'',pay_type:'monthly',compensation_class:'staff',base_salary:'',daily_rate:'',hourly_rate:'',bank_name:'',bank_account_no:'',bank_account_name:'',date_joined:''});
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.name) { setError('Name required'); return; }
    if (!['staff','manager'].includes(form.compensation_class)) {setError('Select Staff or Manager for the extra-time rate class.');return;}
    setSaving(true);
    try {
      if (editStaff) await put('/payroll/staff/'+editStaff.id, form);
      else await post('/payroll/staff', form);
      setShowForm(false);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:680}}>
        <div className="modal-hdr">
          <span className="modal-title">Manage Staff</span>
          <div className="flex-center gap-sm">
            {!showForm && <button className="btn btn-primary btn-sm" onClick={openNew}>+ Add Staff</button>}
            <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
          </div>
        </div>
        <div className="modal-body">
          {!showForm ? (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr style={{background:'var(--cream)'}}>
                <th style={{padding:'8px 12px',textAlign:'left',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Name</th>
                <th style={{padding:'8px 12px',textAlign:'left',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Role</th>
                <th style={{padding:'8px 12px',textAlign:'left',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Pay Type</th>
                <th style={{padding:'8px 12px',textAlign:'left',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Rate Class</th>
                <th style={{padding:'8px 12px',textAlign:'right',fontSize:10,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase'}}>Base</th>
                <th></th>
              </tr></thead>
              <tbody>
                {staff.length===0
                  ? <tr><td colSpan="6"><div className="empty-state" style={{padding:24}}><p>No staff added yet</p></div></td></tr>
                  : staff.map(s => (
                    <tr key={s.id}>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid var(--border-light)',fontWeight:500}}>{s.name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid var(--border-light)',color:'#555'}}>{s.role||'—'}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid var(--border-light)'}}><span className="badge b-grey">{s.pay_type}</span></td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid var(--border-light)'}}>{s.compensation_class==='manager'?'Manager':'Staff'}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid var(--border-light)',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:14}}>
                        {s.pay_type==='monthly'?fmt(s.base_salary):s.pay_type==='daily'?fmt(s.daily_rate)+'/day':fmt(s.hourly_rate)+'/hr'}
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid var(--border-light)'}}>
                        <div className="flex-center gap-sm">
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(s)}>Edit</button>
                          <button className="btn btn-outline btn-sm" style={{color:'var(--unpaid)',borderColor:'var(--unpaid)'}}
                            onClick={async () => {
                              if (!window.confirm('Deactivate '+s.name+'? They will no longer appear in payroll runs.')) return;
                              try { await put('/payroll/staff/'+s.id, {...s, is_active:false}); onSaved(); }
                              catch (err) { alert(err.message); }
                            }}>Deactivate</button>
                        </div>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          ) : (
            <div>
              {error && <div className="alert alert-error mb-md">{error}</div>}
              <div className="form-row">
                <div className="form-group"><label className="form-label">Full Name *</label>
                  <input className="form-control" value={form.name||''} onChange={e=>setForm({...form,name:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Role / Position</label>
                  <input className="form-control" placeholder="e.g. Production Staff, Driver" value={form.role||''} onChange={e=>setForm({...form,role:e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Department</label>
                  <input className="form-control" placeholder="e.g. Production, Admin" value={form.department||''} onChange={e=>setForm({...form,department:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Date Joined</label>
                  <input type="date" className="form-control" value={form.date_joined||''} onChange={e=>setForm({...form,date_joined:e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Phone</label>
                  <input className="form-control" value={form.phone||''} onChange={e=>setForm({...form,phone:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Email</label>
                  <input className="form-control" value={form.email||''} onChange={e=>setForm({...form,email:e.target.value})} /></div>
              </div>
              <div className="divider"></div>
              <div className="form-group"><label className="form-label">Pay Type</label>
                <select className="form-control" value={form.pay_type||'monthly'} onChange={e=>setForm({...form,pay_type:e.target.value})}>
                  <option value="monthly">Monthly Salary</option>
                  <option value="daily">Daily Rate</option>
                  <option value="hourly">Hourly Rate</option>
                </select>
              </div>
              <div className="form-group"><label className="form-label">Extra-time rate class</label>
                <select className="form-control" aria-label="Extra-time rate class" value={form.compensation_class||'staff'} onChange={event=>setForm({...form,compensation_class:event.target.value})}>
                  <option value="staff">Staff</option><option value="manager">Manager</option>
                </select>
                <div className="form-hint">Select which per-minute overtime, night and weekend rates apply. This does not change the person's system access role.</div>
              </div>
              {form.pay_type==='monthly' && (
                <div className="form-group"><label className="form-label">Monthly Salary (N)</label>
                  <input type="number" className="form-control" disabled={!!form.salary_locked} value={form.base_salary||''} onChange={e=>setForm({...form,base_salary:e.target.value})} />{form.salary_locked && <p className="text-muted text-sm">Base salary follows Staff Grades and dated approval history.</p>}</div>
              )}
              {form.pay_type==='daily' && (
                <div className="form-group"><label className="form-label">Daily Rate (N)</label>
                  <input type="number" className="form-control" value={form.daily_rate||''} onChange={e=>setForm({...form,daily_rate:e.target.value})} /></div>
              )}
              {form.pay_type==='hourly' && (
                <div className="form-group"><label className="form-label">Hourly Rate (N)</label>
                  <input type="number" className="form-control" value={form.hourly_rate||''} onChange={e=>setForm({...form,hourly_rate:e.target.value})} /></div>
              )}
              <div className="divider"></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Bank Name</label>
                  <input className="form-control" value={form.bank_name||''} onChange={e=>setForm({...form,bank_name:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Account Number</label>
                  <input className="form-control" value={form.bank_account_no||''} onChange={e=>setForm({...form,bank_account_no:e.target.value})} /></div>
              </div>
              <div className="form-group"><label className="form-label">Account Name</label>
                <input className="form-control" value={form.bank_account_name||''} onChange={e=>setForm({...form,bank_account_name:e.target.value})} /></div>
              <div className="form-group" style={{marginTop:8}}>
                <label className="form-label">Link to System User (optional)</label>
                <select className="form-control" value={form.user_id||''} onChange={e=>setForm({...form,user_id:e.target.value})}>
                  <option value="">Not linked to a system account</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role.replace(/_/g,' ')})</option>)}
                </select>
                <div className="form-hint">Link this staff to their login account for payslip access</div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-ftr">
          {showForm ? (
            <>
              <button className="btn btn-outline" onClick={() => setShowForm(false)}>Back</button>
              <button className="btn btn-primary" onClick={submit} disabled={saving}>
                {saving?'Saving...':editStaff?'Save Changes':'Add Staff'}
              </button>
            </>
          ) : (
            <button className="btn btn-outline" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

function PayrollEntryModal({ entry, runId, staff, onClose, onSaved }) {
  const isEdit = !!entry?.id;
  const [form, setForm] = useState({
    staff_id: entry?.staff_id||'',
    working_days: entry?.working_days||22,
    days_worked: entry?.days_worked||'',
    hours_worked: entry?.hours_worked||'',
    base_pay: entry?.base_pay||'',
    notes: entry?.notes||''
  });
  const [items, setItems] = useState(entry?.items||[]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedStaff = staff.find(s => s.id === form.staff_id);

  // Auto calculate base pay when days worked changes
  const calcBasePay = (staffMember, daysWorked, workingDays) => {
    if (!staffMember) return 0;
    if (staffMember.pay_type === 'monthly' && daysWorked && workingDays) {
      return ((parseFloat(daysWorked) / parseFloat(workingDays)) * parseFloat(staffMember.base_salary||0)).toFixed(2);
    }
    if (staffMember.pay_type === 'daily' && daysWorked) {
      return (parseFloat(daysWorked) * parseFloat(staffMember.daily_rate||0)).toFixed(2);
    }
    if (staffMember.pay_type === 'hourly' && form.hours_worked) {
      return (parseFloat(form.hours_worked) * parseFloat(staffMember.hourly_rate||0)).toFixed(2);
    }
    return staffMember.base_salary||0;
  };

  const handleStaffChange = (id) => {
    const s = staff.find(x => x.id===id);
    const bp = calcBasePay(s, form.days_worked, form.working_days);
    setForm({...form, staff_id:id, base_pay:bp});
  };

  const handleDaysChange = (days) => {
    const bp = calcBasePay(selectedStaff, days, form.working_days);
    setForm({...form, days_worked:days, base_pay:bp});
  };

  const handleWorkingDaysChange = (wd) => {
    const bp = calcBasePay(selectedStaff, form.days_worked, wd);
    setForm({...form, working_days:wd, base_pay:bp});
  };

  const handleHoursChange = (hours) => {
    const bp = calcBasePay(selectedStaff, form.days_worked, form.working_days);
    const hourlyPay = selectedStaff ? parseFloat(hours||0)*parseFloat(selectedStaff.hourly_rate||0) : 0;
    setForm({...form, hours_worked:hours, base_pay:selectedStaff?.pay_type==='hourly'?hourlyPay.toFixed(2):bp});
  };

  const automatedItem = item => Boolean(item.source_id || item.source_type || item.source_user_id || item.payable_minutes != null || item.rate_per_minute != null);
  const addItem = (type) => {
    setItems([...items, {type, label:'', amount:'', category:type==='addition'?'other_earning':'other_deduction'}]);
  };

  const updateItem = (i, field, val) => {
    if (automatedItem(items[i])) return;
    const updated = [...items];
    updated[i] = {...updated[i], [field]:val};
    setItems(updated);
  };

  const removeItem = (i) => { if(!automatedItem(items[i])) setItems(items.filter((_,idx) => idx!==i)); };
  const chooseEarning = (index, value) => {
    if (automatedItem(items[index])) return;
    const labels={birthday_bonus:'Birthday Bonus',bonus:'Bonus',allowance:'Allowance',overtime:'Overtime',night_shift:'Night Shift',weekend_shift:'Weekend Shift',other_earning:'Other Earning'};
    setItems(current=>current.map((item,i)=>i===index?{...item,category:value==='birthday_bonus'?'bonus':value,label:labels[value]||item.label}:item));
  };

  const additions = items.filter(i=>i.type==='addition').reduce((s,i)=>s+parseFloat(i.amount||0),0);
  const deductions = items.filter(i=>i.type==='deduction').reduce((s,i)=>s+parseFloat(i.amount||0),0);
  const gross = parseFloat(form.base_pay||0) + additions;
  const net = gross - deductions;

  const submit = async () => {
    if (!form.staff_id) { setError('Select a staff member'); return; }
    setSaving(true);
    try {
      const payload = { ...form, items: items.filter(i=>i.label && i.amount!=='' && i.amount!=null) };
      if (isEdit) await put('/payroll/entries/'+entry.id, payload);
      else await post('/payroll/runs/'+runId+'/entries', payload);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:680}}>
        <div className="modal-hdr">
          <span className="modal-title">{isEdit?'Edit':'Add'} Payroll Entry</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}

          <div className="form-group">
            <label className="form-label">Staff Member *</label>
            <select className="form-control" value={form.staff_id} onChange={e=>handleStaffChange(e.target.value)} disabled={isEdit}>
              <option value="">Select staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.name} — {s.role||s.department||'Staff'}</option>)}
            </select>
          </div>

          {selectedStaff && (
            <div style={{background:'#F2EFE7',borderRadius:8,padding:12,marginBottom:14,fontSize:12}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                <div><span style={{color:'#8A8A8A'}}>Pay Type: </span><strong style={{textTransform:'capitalize'}}>{selectedStaff.pay_type}</strong></div>
                {selectedStaff.pay_type==='monthly' && <div><span style={{color:'#8A8A8A'}}>Monthly Salary: </span><strong>{fmt(selectedStaff.base_salary)}</strong></div>}
                {selectedStaff.pay_type==='daily' && <div><span style={{color:'#8A8A8A'}}>Daily Rate: </span><strong>{fmt(selectedStaff.daily_rate)}</strong></div>}
                {selectedStaff.pay_type==='hourly' && <div><span style={{color:'#8A8A8A'}}>Hourly Rate: </span><strong>{fmt(selectedStaff.hourly_rate)}</strong></div>}
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Working Days in Month</label>
              <input type="number" className="form-control" value={form.working_days}
                onChange={e=>handleWorkingDaysChange(e.target.value)}
                placeholder="e.g. 22" />
              <div className="form-hint">Total working days this month</div>
            </div>
            <div className="form-group">
              <label className="form-label">Days Worked</label>
              <input type="number" className="form-control" value={form.days_worked||''}
                onChange={e=>handleDaysChange(e.target.value)}
                placeholder="e.g. 20" />
              <div className="form-hint">Actual days this staff worked</div>
            </div>
          </div>

          {selectedStaff?.pay_type==='hourly' && (
            <div className="form-group">
              <label className="form-label">Hours Worked</label>
              <input type="number" className="form-control" value={form.hours_worked||''}
                onChange={e=>handleHoursChange(e.target.value)} />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Base Pay (N)</label>
            <input type="number" className="form-control" value={form.base_pay||''}
              onChange={e=>setForm({...form,base_pay:e.target.value})}
              style={{fontWeight:600,fontSize:15}} />
            <div className="form-hint">Auto-calculated. You can override manually.</div>
          </div>

          <div className="divider"></div>

          {/* Additions */}
          <div style={{marginBottom:16}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'var(--paid)'}}>Additions</div>
              <button className="btn btn-outline btn-sm" style={{color:'var(--paid)',borderColor:'var(--paid)'}} onClick={()=>addItem('addition')}>+ Add</button>
            </div>
            {items.filter(i=>i.type==='addition').length===0 && (
              <div style={{fontSize:12,color:'#8A8A8A',fontStyle:'italic'}}>No additions yet — click Add for overtime, bonus, allowances etc.</div>
            )}
            {items.map((item,i) => item.type!=='addition' ? null : (
              <div key={i} style={{display:'grid',gridTemplateColumns:'1.3fr 2fr 1fr auto',gap:8,marginBottom:8,alignItems:'center'}}>
                <select className="form-control" aria-label="Earning category" disabled={automatedItem(item)}
                  value={/birthday/i.test(item.label||'')?'birthday_bonus':item.category||'other_earning'} onChange={event=>chooseEarning(i,event.target.value)}>
                  <option value="other_earning">Other earning</option><option value="birthday_bonus">Birthday bonus</option><option value="bonus">Bonus</option><option value="allowance">Allowance</option><option value="overtime">Overtime</option><option value="night_shift">Night shift</option><option value="weekend_shift">Weekend work</option>
                </select>
                <input className="form-control" style={{fontSize:12}} placeholder="e.g. Overtime, Performance Bonus, Transport Allowance"
                  value={item.label} readOnly={automatedItem(item)} title={automatedItem(item)?'Calculated item: use the payroll review to update':''} onChange={e=>updateItem(i,'label',e.target.value)} />
                <input type="number" className="form-control" style={{fontSize:12}} placeholder="Amount"
                  value={item.amount} readOnly={automatedItem(item)} onChange={e=>updateItem(i,'amount',e.target.value)} />
                <button className="btn btn-ghost btn-sm" style={{color:'var(--ink-light)'}} disabled={automatedItem(item)} onClick={()=>removeItem(i)}>X</button>
              </div>
            ))}
          </div>

          {/* Deductions */}
          <div style={{marginBottom:16}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'var(--unpaid)'}}>Deductions</div>
              <button className="btn btn-outline btn-sm" style={{color:'var(--unpaid)',borderColor:'var(--unpaid)'}} onClick={()=>addItem('deduction')}>+ Add</button>
            </div>
            {items.filter(i=>i.type==='deduction').length===0 && (
              <div style={{fontSize:12,color:'#8A8A8A',fontStyle:'italic'}}>No deductions yet. Calculate statutory and attendance deductions through the payroll review; use Add for a separately authorized adjustment.</div>
            )}
            {items.map((item,i) => item.type!=='deduction' ? null : (
              <div key={i} style={{display:'grid',gridTemplateColumns:'2fr 1fr auto',gap:8,marginBottom:8,alignItems:'center'}}>
                <input className="form-control" style={{fontSize:12}} placeholder="Description of authorized deduction"
                  value={item.label} readOnly={automatedItem(item)} title={automatedItem(item)?'Calculated item: use the payroll review to update':''} onChange={e=>updateItem(i,'label',e.target.value)} />
                <input type="number" className="form-control" style={{fontSize:12}} placeholder="Amount"
                  value={item.amount} readOnly={automatedItem(item)} onChange={e=>updateItem(i,'amount',e.target.value)} />
                <button className="btn btn-ghost btn-sm" style={{color:'var(--ink-light)'}} disabled={automatedItem(item)} onClick={()=>removeItem(i)}>X</button>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div style={{background:'#F2EFE7',borderRadius:8,padding:14}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:10,fontSize:13}}>
              <div>
                <div style={{fontSize:9,color:'#8A8A8A',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Base Pay</div>
                <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.1rem',fontWeight:600}}>{fmt(form.base_pay||0)}</div>
              </div>
              <div>
                <div style={{fontSize:9,color:'var(--paid)',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Additions</div>
                <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.1rem',fontWeight:600,color:'var(--paid)'}}>+{fmt(additions)}</div>
              </div>
              <div>
                <div style={{fontSize:9,color:'var(--unpaid)',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Deductions</div>
                <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.1rem',fontWeight:600,color:'var(--unpaid)'}}>-{fmt(deductions)}</div>
              </div>
              <div>
                <div style={{fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Net Pay</div>
                <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:700,color:'#556B2F'}}>{fmt(net)}</div>
              </div>
            </div>
          </div>

          <div className="form-group" style={{marginTop:14}}>
            <label className="form-label">Notes</label>
            <input className="form-control" value={form.notes||''} onChange={e=>setForm({...form,notes:e.target.value})} />
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving?'Saving...':isEdit?'Save Changes':'Add to Payroll'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PayslipModal({ entry, run, onClose }) {
  const printPayslip = () => {
    const w = window.open('', '_blank');
    const additions = (entry.items||[]).filter(i=>i.type==='addition');
    const deductions = (entry.items||[]).filter(i=>i.type==='deduction');
    const addRows = additions.map(i => `
      <tr><td style="padding:6px 12px;border-bottom:1px solid #EDE7D8">${i.label}</td>
      <td style="padding:6px 12px;border-bottom:1px solid #EDE7D8;text-align:right;color:#2E6B3A;font-family:serif">+${fmt(i.amount)}</td></tr>`).join('');
    const dedRows = deductions.map(i => `
      <tr><td style="padding:6px 12px;border-bottom:1px solid #EDE7D8">${i.label}</td>
      <td style="padding:6px 12px;border-bottom:1px solid #EDE7D8;text-align:right;color:#8F2E24;font-family:serif">-${fmt(i.amount)}</td></tr>`).join('');

    w.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8"><title>Payslip - ${entry.staff_name}</title>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
      <style>body{font-family:Inter,sans-serif;margin:0;padding:0}@media print{body{margin:0}}</style>
      </head><body>
      <div style="max-width:480px;margin:0 auto;border:1px solid #EDE7D8;border-radius:10px;overflow:hidden">
        <div style="background:#556B2F;padding:18px 24px;display:flex;justify-content:space-between;align-items:center">
          <div>
            <div style="font-family:Cormorant Garamond,serif;font-size:1.3rem;font-weight:600;color:#A8893D">BLOANS CREATIONS LTD</div>
            <div style="font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:rgba(242,239,231,.5);margin-top:2px">Payslip</div>
          </div>
          <div style="text-align:right;font-size:12px;color:rgba(242,239,231,.6)">
            <div style="font-family:Cormorant Garamond,serif;font-size:1.1rem;color:#A8893D">${MONTHS[(run?.month||1)-1]} ${run?.year||''}</div>
          </div>
        </div>

        <div style="padding:16px 24px;background:#F2EFE7;border-bottom:1px solid #DDD4C0">
          <div style="font-weight:600;font-size:15px;color:#3B2A21">${entry.staff_name}</div>
          <div style="font-size:12px;color:#555;margin-top:3px">${entry.staff_role||''} ${entry.department?'| '+entry.department:''}</div>
          ${entry.bank_name?`<div style="font-size:11px;color:#8A8A8A;margin-top:3px">${entry.bank_name} | ${entry.bank_account_no} | ${entry.bank_account_name}</div>`:''}
        </div>

        <div style="padding:16px 24px">
          <table style="width:100%;border-collapse:collapse;font-size:13px">
            <tr style="background:#f9f9f9">
              <td style="padding:8px 12px;border-bottom:1px solid #EDE7D8;font-weight:500">Base Pay</td>
              <td style="padding:8px 12px;border-bottom:1px solid #EDE7D8;text-align:right;font-family:serif;font-size:14px">${fmt(entry.base_pay)}</td>
            </tr>
            ${entry.days_worked?`<tr><td style="padding:6px 12px;border-bottom:1px solid #EDE7D8;color:#8A8A8A;font-size:12px">Days Worked</td><td style="padding:6px 12px;border-bottom:1px solid #EDE7D8;text-align:right;font-size:12px">${entry.days_worked} days</td></tr>`:''}
            ${addRows}
            ${dedRows}
          </table>

          <div style="margin-top:12px;padding:14px;background:#F2EFE7;border-radius:8px">
            <div style="display:flex;justify-content:space-between;margin-bottom:6px;font-size:13px">
              <span style="color:#8A8A8A">Gross Pay</span><span style="font-family:serif">${fmt(entry.gross_pay)}</span></div>
            <div style="display:flex;justify-content:space-between;margin-bottom:8px;font-size:13px">
              <span style="color:#8A8A8A">Total Deductions</span><span style="font-family:serif;color:#8F2E24">-${fmt(entry.total_deductions)}</span></div>
            <div style="display:flex;justify-content:space-between;padding-top:8px;border-top:1px solid #DDD4C0">
              <span style="font-weight:700;font-size:14px;color:#3B2A21">NET PAY</span>
              <span style="font-family:Cormorant Garamond,serif;font-size:1.5rem;font-weight:700;color:#556B2F">${fmt(entry.net_pay)}</span>
            </div>
          </div>

          ${entry.notes?`<div style="margin-top:10px;font-size:11px;color:#8A8A8A">Notes: ${entry.notes}</div>`:''}
        </div>

        <div style="padding:10px 24px;background:#556B2F;text-align:center">
          <div style="font-size:10px;color:rgba(242,239,231,.5)">This is a computer generated payslip — Bloans Creations Ltd</div>
        </div>
      </div>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`);
    w.document.close();
  };

  const additions = (entry.items||[]).filter(i=>i.type==='addition');
  const deductions = (entry.items||[]).filter(i=>i.type==='deduction');

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:480}}>
        <div className="modal-hdr">
          <span className="modal-title">Payslip — {entry.staff_name}</span>
          <div className="flex-center gap-sm">
            <button className="btn btn-primary btn-sm" onClick={printPayslip}>Print Payslip</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
          </div>
        </div>
        <div className="modal-body">
          <div style={{background:'#F2EFE7',borderRadius:8,padding:14,marginBottom:14}}>
            <div style={{fontWeight:600,fontSize:14,color:'#3B2A21'}}>{entry.staff_name}</div>
            <div style={{fontSize:12,color:'#555',marginTop:2}}>{entry.staff_role||''}</div>
            {entry.days_worked && <div style={{fontSize:12,color:'#8A8A8A',marginTop:4}}>Days worked: {entry.days_worked}</div>}
          </div>

          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13,marginBottom:14}}>
            <tbody>
              <tr style={{background:'#f9f9f9'}}>
                <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>Base Pay</td>
                <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',fontFamily:'Cormorant Garamond,serif',fontSize:14}}>{fmt(entry.base_pay)}</td>
              </tr>
              {additions.map((item,i) => (
                <tr key={i}>
                  <td style={{padding:'6px 12px',borderBottom:'1px solid #EDE7D8',color:'var(--paid)'}}>{item.label}</td>
                  <td style={{padding:'6px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',color:'var(--paid)',fontFamily:'Cormorant Garamond,serif',fontSize:13}}>+{fmt(item.amount)}</td>
                </tr>
              ))}
              {deductions.map((item,i) => (
                <tr key={i}>
                  <td style={{padding:'6px 12px',borderBottom:'1px solid #EDE7D8',color:'var(--unpaid)'}}>{item.label}</td>
                  <td style={{padding:'6px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'right',color:'var(--unpaid)',fontFamily:'Cormorant Garamond,serif',fontSize:13}}>-{fmt(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{background:'#F2EFE7',borderRadius:8,padding:14}}>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:6}}>
              <span style={{color:'#8A8A8A'}}>Gross Pay</span><span>{fmt(entry.gross_pay)}</span></div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:8}}>
              <span style={{color:'#8A8A8A'}}>Total Deductions</span>
              <span style={{color:'var(--unpaid)'}}>-{fmt(entry.total_deductions)}</span></div>
            <div style={{display:'flex',justifyContent:'space-between',paddingTop:8,borderTop:'1px solid #DDD4C0'}}>
              <span style={{fontWeight:700,fontSize:14,color:'#3B2A21'}}>NET PAY</span>
              <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.5rem',fontWeight:700,color:'#556B2F'}}>{fmt(entry.net_pay)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
